/**
 * Ephemeral Files Service - Temporary File Operations
 * Ephemeral files are content-addressed (by SHA-256 hash), globally shared,
 * and automatically expire after 24 hours.
 */

import type { GatewayClient } from './client'
import type { UploadProgressCallback } from './types'

// ============================================================================
// Types
// ============================================================================

export interface EphemeralFileResponse {
  /** File reference for use in action parameters (e{hash} format) */
  fileRef: string
  /** Raw SHA-256 hash (without e prefix) */
  hash: string
  size: number
  contentType: string
  expiresAt?: string
}

export interface EphemeralUploadURLRequest {
  hash: string
  contentType?: string
}

export interface EphemeralUploadURLResponse {
  uploadUrl: string
  hash: string
  expiresAt?: string
  contentType: string
  exists: boolean
}

export interface EphemeralDownloadURLResponse {
  downloadUrl: string
  hash: string
  expiresAt: string
  size: number
  contentType: string
}

export interface FileUploadOptions {
  onProgress?: UploadProgressCallback
}

export interface EphemeralFilesService {
  /**
   * Upload an ephemeral file (expires after 24 hours)
   */
  upload(
    file: File | Blob,
    options?: FileUploadOptions
  ): Promise<EphemeralFileResponse>

  /**
   * Check if ephemeral file exists and get metadata
   */
  getMetadata(hash: string): Promise<EphemeralFileResponse>

  /**
   * Get content URL for an ephemeral file
   */
  getContentUrl(hash: string): string

  /**
   * Get signed download URL for an ephemeral file
   */
  getDownloadUrl(hash: string): Promise<EphemeralDownloadURLResponse>

  /**
   * Get signed upload URL for direct upload (client computes hash first)
   */
  getUploadUrl(
    hash: string,
    contentType?: string
  ): Promise<EphemeralUploadURLResponse>
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create an EphemeralFilesService instance
 */
export function createEphemeralFilesService(
  client: GatewayClient
): EphemeralFilesService {
  const baseUrl = client.config.baseUrl

  return {
    async upload(
      file: File | Blob,
      options?: FileUploadOptions
    ): Promise<EphemeralFileResponse> {
      const response = await client.uploadFile(
        '/v1/ephemeral',
        file,
        {},
        options?.onProgress
      )
      return response.json()
    },

    async getMetadata(hash: string): Promise<EphemeralFileResponse> {
      return client.get<EphemeralFileResponse>(`/v1/ephemeral/${hash}`)
    },

    getContentUrl(hash: string): string {
      return `${baseUrl}/v1/ephemeral/${hash}/content`
    },

    async getDownloadUrl(hash: string): Promise<EphemeralDownloadURLResponse> {
      return client.get<EphemeralDownloadURLResponse>(
        `/v1/ephemeral/${hash}/download-url`
      )
    },

    async getUploadUrl(
      hash: string,
      contentType?: string
    ): Promise<EphemeralUploadURLResponse> {
      return client.post<EphemeralUploadURLResponse>(
        '/v1/ephemeral/upload-url',
        { hash, contentType }
      )
    },
  }
}

// Re-export for backward compatibility during migration
export type FilesService = EphemeralFilesService
export const createFilesService = createEphemeralFilesService
